import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import {
    GeometryQuestionOptions,
    POLYGONS,
    ProblemData,
} from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import * as enGeometry from "../Files/Prompts/en/geometry.ts";
import * as esGeometry from "../Files/Prompts/es/geometry.ts";
import * as frGeometry from "../Files/Prompts/fr/geometry.ts";

const GEOMETRY: Record<PrintedLanguage, typeof enGeometry> = {
    en: enGeometry,
    es: esGeometry,
    fr: frGeometry,
};

/**
 * Geometry. See ADR 0049.
 *
 * The format draws nothing. Every figure is described in words - "two angles
 * of a triangle measure 45° and 65°" - which is what lets the whole strand
 * reuse the prompt-and-answer renderer and is the reason ADR 0022 §2's
 * refusal could be narrowed at all (ADR 0049 §1).
 *
 * Every arm builds from its answer, the construction ADR 0032 §4, 0034 §3 and
 * 0035 all use. Here it is what guarantees the key: an angle sum that is
 * assembled from its parts is exact, where one drawn and then subtracted can
 * land on a value the sheet did not intend.
 */

/** The degree sign, written once so a stray ASCII "deg" cannot creep in. */
const DEG = "°";

type Built = { prompt: string; answer: string };

function questionTypes(question: GeometryQuestionOptions): GeometryQuestionOptions[] {
    if (question === GeometryQuestionOptions.Mixed) {
        return [
            GeometryQuestionOptions.ClassifyAngle,
            GeometryQuestionOptions.MissingAngle,
            GeometryQuestionOptions.ComplementSupplement,
            GeometryQuestionOptions.ClassifyTriangle,
            GeometryQuestionOptions.PolygonSides,
        ];
    }
    return [question];
}

/** A usable angle step: one of the offered values, defaulting to five. */
function usableStep(step: number): number {
    const value = Math.floor(step);
    return value === 10 || value === 5 || value === 1 ? value : 5;
}

/** A multiple of `step` in [min, max], inclusive of both ends when they are multiples. */
function angleOnGrid(min: number, max: number, step: number): number {
    const lowest = Math.ceil(min / step);
    const highest = Math.floor(max / step);
    if (highest < lowest) {
        return min;
    }
    return getRandomInt(lowest, highest) * step;
}

/**
 * Classify an angle by its measure.
 *
 * **The class is drawn first, then a measure inside it.** Drawing a measure
 * and classifying whatever came out makes `right` and `straight` - which are
 * exactly one value each - almost never appear at a step of one: a sheet with
 * two of its four answers effectively absent. Every offered step divides 90
 * and 180, so both are always reachable.
 */
function buildClassifyAngle(step: number, language: PrintedLanguage): Built {
    const classes = ["acute", "right", "obtuse", "straight"] as const;
    const kind = classes[getRandomInt(0, classes.length - 1)];

    let measure: number;
    switch (kind) {
        case "right":
            measure = 90;
            break;
        case "straight":
            measure = 180;
            break;
        case "obtuse":
            // Strictly between 90 and 180.
            measure = angleOnGrid(90 + step, 180 - step, step);
            break;
        default:
            measure = angleOnGrid(step, 90 - step, step);
            break;
    }

    return {
        prompt: GEOMETRY[language].geometryPrompts.classifyAngle(measure),
        answer: GEOMETRY[language].angleClassLabels[kind],
    };
}

/**
 * A missing angle in a triangle, on a straight line, or around a point.
 *
 * Built by drawing the parts and letting the last one be what is left, so the
 * sum is exact by construction. The answer is never zero: each part is at
 * least one step, and the asked-for angle is drawn before the others.
 */
function buildMissingAngle(step: number, language: PrintedLanguage): Built | null {
    const kind = getRandomInt(0, 2);
    const t = GEOMETRY[language].geometryPrompts;

    if (kind === 0) {
        // A triangle: three angles summing to 180.
        const first = angleOnGrid(step, 180 - 2 * step, step);
        const second = angleOnGrid(step, 180 - first - step, step);
        const third = 180 - first - second;
        if (third < step) {
            return null;
        }
        return {
            prompt: t.missingAngleTriangle(first, second),
            answer: `${third}${DEG}`,
        };
    }

    if (kind === 1) {
        // Two angles on a straight line.
        const first = angleOnGrid(step, 180 - step, step);
        return {
            prompt: t.missingAngleLine(first),
            answer: `${180 - first}${DEG}`,
        };
    }

    // Three angles around a point.
    const first = angleOnGrid(step, 360 - 2 * step, step);
    const second = angleOnGrid(step, 360 - first - step, step);
    const third = 360 - first - second;
    if (third < step) {
        return null;
    }
    return {
        prompt: t.missingAnglePoint(first, second),
        answer: `${third}${DEG}`,
    };
}

/**
 * A complement (to 90) or a supplement (to 180).
 *
 * The answer is drawn first and the question derived from it, so the answer is
 * never zero - the complement of 90 and the supplement of 180 are both 0, and
 * neither is a question worth printing.
 */
function buildComplementSupplement(step: number, language: PrintedLanguage): Built {
    const supplementary = getRandomInt(0, 1) === 1;
    const total = supplementary ? 180 : 90;
    const answer = angleOnGrid(step, total - step, step);
    const given = total - answer;
    const t = GEOMETRY[language].geometryPrompts;
    return {
        prompt: supplementary ? t.supplement(given) : t.complement(given),
        answer: `${answer}${DEG}`,
    };
}

/**
 * Classify a triangle by its side lengths.
 *
 * **Equilateral is the answer when all three sides are equal**, not isosceles.
 * Both are defensible in general - an equilateral triangle is a special
 * isosceles one - so the sheet asks for the most specific name and the FAQ
 * copy says so. It is the one convention this format carries.
 *
 * Every triple satisfies the triangle inequality. `2, 2, 9` is not a triangle,
 * and asking a student to classify it is wrong rather than merely hard.
 *
 * The angle step does not apply here: these are side lengths, not angles.
 */
function buildClassifyTriangle(language: PrintedLanguage): Built | null {
    const kinds = ["equilateral", "isosceles", "scalene"] as const;
    const kind = kinds[getRandomInt(0, kinds.length - 1)];
    let sides: number[];

    if (kind === "equilateral") {
        const side = getRandomInt(2, 20);
        sides = [side, side, side];
    } else if (kind === "isosceles") {
        // Two equal legs and a different base, with base < 2 * leg so the
        // triangle closes.
        const leg = getRandomInt(3, 20);
        const base = getRandomInt(2, 2 * leg - 1);
        if (base === leg) {
            return null;
        }
        sides = shuffleTriple([leg, leg, base]);
    } else {
        const a = getRandomInt(2, 18);
        const b = getRandomInt(a + 1, a + 8);
        // Strictly between |b - a| and b + a, and different from both.
        const c = getRandomInt(b - a + 1, b + a - 1);
        if (c === a || c === b) {
            return null;
        }
        sides = shuffleTriple([a, b, c]);
    }

    if (!isTriangle(sides)) {
        return null;
    }

    return {
        prompt: GEOMETRY[language].geometryPrompts.classifyTriangle(sides[0], sides[1], sides[2]),
        answer: GEOMETRY[language].triangleClassLabels[kind],
    };
}

/** The triangle inequality, checked on the longest side. */
export function isTriangle(sides: number[]): boolean {
    const [a, b, c] = [...sides].sort((x, y) => x - y);
    return a > 0 && a + b > c;
}

/** A small fixed-size shuffle, so the equal sides are not always first. */
function shuffleTriple(values: number[]): number[] {
    const out = [...values];
    for (let i = out.length - 1; i > 0; i--) {
        const j = getRandomInt(0, i);
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}

/** A polygon's name from its side count, or its side count from its name. */
function buildPolygonSides(language: PrintedLanguage): Built {
    const polygon = POLYGONS[getRandomInt(0, POLYGONS.length - 1)];
    const { geometryPrompts: t, polygonNames } = GEOMETRY[language];
    const name = polygonNames[polygon.sides] ?? polygon.name;
    if (getRandomInt(0, 1) === 1) {
        return {
            prompt: t.polygonSidesFromName(name),
            answer: String(polygon.sides),
        };
    }
    return {
        prompt: t.polygonNameFromSides(polygon.sides),
        answer: name,
    };
}

/**
 * One geometry problem, or `null` when the draw was not worth printing - a
 * spent attempt the shared loop retries (ADR 0042).
 */
export function buildGeometryProblem(
    question: GeometryQuestionOptions,
    angleStep: number,
    key: number,
    language: PrintedLanguage = 'en'
): ProblemData | null {
    const step = usableStep(angleStep);
    let built: Built | null;

    switch (question) {
        case GeometryQuestionOptions.MissingAngle:
            built = buildMissingAngle(step, language);
            break;
        case GeometryQuestionOptions.ComplementSupplement:
            built = buildComplementSupplement(step, language);
            break;
        case GeometryQuestionOptions.ClassifyTriangle:
            built = buildClassifyTriangle(language);
            break;
        case GeometryQuestionOptions.PolygonSides:
            built = buildPolygonSides(language);
            break;
        default:
            built = buildClassifyAngle(step, language);
            break;
    }

    if (built === null) {
        return null;
    }

    return {
        key,
        numbers: [],
        operator: "",
        display: [built.prompt, built.answer],
        geometryQuestion: question,
    };
}

/** A page-shaped set of geometry problems. */
export function generateGeometryPages(settings: ISettingsProps): ProblemData[][] | null {
    const types = questionTypes(settings.GeometryQuestion ?? GeometryQuestionOptions.Mixed);
    const angleStep = settings.GeometryAngleStep ?? 5;
    const language = printedLanguageOf(settings);
    return generateProblemPages({
        settings,
        choices: types,
        build: (question, key) => buildGeometryProblem(question, angleStep, key, language),
        fingerprint: (problem) => problem.display![0],
    });
}
